import { useTranslations } from 'next-intl'

import { PopularOption } from '@/entities/map'
import { AdditionalOption, BasicOption, RoomIcon } from '@/entities/room'
import { cn } from '@/shared/lib'

import { useOptions } from './option'
import { ExpandButton, useExpand } from '../../../lib/expand'
import { FilterType, useFilter } from '../../../model/filter'
import { Title, List, Item, SubTitle } from '../../common/layout'

const BASIC_OPTIONS_INITIAL_COUNT = 6

type Props = {
  type: FilterType
  ref: (element: HTMLDivElement) => void
  className?: string
  defaultExpanded: boolean
  selected: (value: PopularOption | BasicOption | AdditionalOption) => boolean
  onExpandClick: () => void
  onChange: ReturnType<typeof useFilter>['updateFilter']
}

export const OptionsInDialogView = ({
  type,
  ref,
  className,
  defaultExpanded,
  selected,
  onExpandClick,
  onChange,
}: Props) => {
  const t = useTranslations('filter')

  const { expanded, toggleExpand } = useExpand({
    defaultExpanded,
  })

  const { POPULAR_OPTIONS, BASIC_OPTIONS, ADDITIONAL_OPTIONS } = useOptions()

  const basicOptionsList = expanded
    ? BASIC_OPTIONS
    : BASIC_OPTIONS.slice(0, BASIC_OPTIONS_INITIAL_COUNT)

  const handleExpandButtonClick = () => {
    toggleExpand()
    onExpandClick()
  }

  const handlePopularOptionChange = (value: PopularOption) => () => {
    onChange({ popularOptions: value })
  }

  const handleBasicOptionChange = (value: BasicOption) => () => {
    onChange({ basicOptions: value })
  }

  const handleAdditionalOptionChange = (value: AdditionalOption) => () => {
    onChange({ additionalOptions: value })
  }

  return (
    <div className={cn('space-y-3', className)} ref={ref}>
      <Title>{t('option.title')}</Title>
      <div>
        <SubTitle>{t('option.popular.title')}</SubTitle>
        <List>
          {POPULAR_OPTIONS.map(({ title, value, icon }) => (
            <Item
              key={title}
              type={type}
              selected={selected(value)}
              onClick={handlePopularOptionChange(value)}
              className="flex pl-3"
              startIcon={<RoomIcon name={icon} size={20} />}
            >
              <p className="ml-1 flex h-11 items-center justify-center whitespace-break-spaces">
                {title}
              </p>
            </Item>
          ))}
        </List>
      </div>
      <div>
        <SubTitle>{t('option.basic.title')}</SubTitle>
        <List>
          {basicOptionsList.map(({ title, value, icon }) => (
            <Item
              key={title}
              type={type}
              selected={selected(value)}
              onClick={handleBasicOptionChange(value)}
              className="flex pl-3"
              startIcon={<RoomIcon name={icon} size={20} />}
            >
              <p className="ml-1 flex h-11 items-center justify-center whitespace-break-spaces">
                {title}
              </p>
            </Item>
          ))}
        </List>
      </div>
      {expanded && (
        <div>
          <SubTitle>{t('option.additional.title')}</SubTitle>
          <List>
            {ADDITIONAL_OPTIONS.map(({ title, value, icon }) => (
              <Item
                key={title}
                type={type}
                selected={selected(value)}
                onClick={handleAdditionalOptionChange(value)}
                className="flex pl-3"
                startIcon={<RoomIcon name={icon} size={20} />}
              >
                <p className="ml-1 flex h-11 items-center justify-center whitespace-break-spaces">
                  {title}
                </p>
              </Item>
            ))}
          </List>
        </div>
      )}

      <div className="mt-4 flex justify-center">
        <ExpandButton expanded={expanded} onClick={handleExpandButtonClick} />
      </div>
    </div>
  )
}
